<template>
  <div class="seqEditor">
    <div v-if="saveError" class="errorBanner">{{ saveError }}</div>

    <div v-if="locked" class="lockedBanner">
      <AppIcon name="agent_guardrail" :size="14" />
      Contacts have been enrolled — structure is locked. You can still edit step guidance.
    </div>

    <!-- Steps -->
    <div class="stepsSection">
      <div class="stepsSectionHeader">
        <div class="stepsSectionTitle">Steps <span class="muted">({{ form.steps.length }})</span></div>
      </div>

      <div class="stepsCanvas">
        <div v-if="form.steps.length === 0 && !locked" class="stepsEmpty">
          <div class="stepsEmptyText">No steps yet</div>
          <button class="btn primary" @click="insertStep(0)">+ Add First Step</button>
        </div>

        <div
          v-if="dragState.draggingIdx !== null && !isNoOpSlot(0)"
          class="stepConnector"
          :class="{ activeSlot: dragState.overSlot === 0 }"
          @dragover.prevent="dragState.overSlot = 0"
          @drop.prevent="onSlotDrop(0)"
        >
          <div v-if="dragState.overSlot === 0" class="dropGhost" />
          <div v-else class="slotHint" />
        </div>

        <template v-for="(step, idx) in form.steps" :key="step._key">
          <div
            class="stepCard"
            :class="[`stepType-${step.step_type}`, { locked, dragging: dragState.draggingIdx === idx, justDropped: justDroppedIdx === idx, justCancelled: justCancelledIdx === idx }]"
            :draggable="!locked"
            @dragstart="onDragStart(idx, $event)"
            @dragend="onDragEnd"
            @dragover.prevent="dragState.overSlot = null"
            @drop.prevent
          >
            <div class="stepFields">
              <div class="stepTypeRow">
                <div class="stepTypeSelectWrap">
                  <SearchSelect
                    :modelValue="step.step_type"
                    :options="stepTypeOptions"
                    :disabled="locked"
                    :allowClear="false"
                    :hideSearch="true"
                    placeholder="Step type"
                    @update:modelValue="(v) => onTypeSelectChange(step, v)"
                  />
                </div>
                <div v-if="step.step_type === 'wait'" class="waitDaysField">
                  <input class="input waitInput" type="number" min="1" :max="step.wait_unit === 'minutes' ? 1440 : 365" v-model.number="step.wait_days" :placeholder="step.wait_unit === 'minutes' ? 'Minutes' : 'Days'" :disabled="locked" />
                  <select class="input waitUnitSelect" v-model="step.wait_unit" :disabled="locked">
                    <option value="days">days</option>
                    <option value="minutes">min</option>
                  </select>
                </div>
              </div>
              <div v-if="step.step_type !== 'wait'" class="stepBriefField">
                <textarea class="stepBriefTextarea" v-model="step.brief" :placeholder="briefPlaceholder(step.step_type)" rows="3" />
              </div>
            </div>
            <button v-if="!locked" class="removeBtn" title="Remove step" @click="removeStep(idx)">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" width="14" height="14">
                <line x1="3" y1="3" x2="13" y2="13"/><line x1="13" y1="3" x2="3" y2="13"/>
              </svg>
            </button>
          </div>

          <div
            class="stepConnector"
            :class="{ activeSlot: dragState.overSlot === idx + 1 }"
            @dragover.prevent="isNoOpSlot(idx + 1) ? (dragState.overSlot = null) : (dragState.overSlot = idx + 1)"
            @drop.prevent="onSlotDrop(idx + 1)"
          >
            <template v-if="dragState.draggingIdx !== null && dragState.overSlot === idx + 1 && !isNoOpSlot(idx + 1)">
              <div class="dropGhost" />
            </template>
            <template v-else>
              <div class="connLine" />
              <button v-if="!locked" class="connInsertBtn" title="Insert step here" @click.stop="insertStep(idx + 1)">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" width="12" height="12">
                  <line x1="8" y1="2" x2="8" y2="14"/><line x1="2" y1="8" x2="14" y2="8"/>
                </svg>
              </button>
              <div v-if="idx < form.steps.length - 1" class="connLine" />
            </template>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, onMounted, onBeforeUnmount } from 'vue'
import AppIcon from '../../components/AppIcon.vue'
import SearchSelect from '../../components/SearchSelect.vue'
import { googleFaviconUrl } from '../../services/favicons.ts'

const linkedinFaviconSmall = googleFaviconUrl('linkedin.com', { size: 32 })

const allStepTypeOptions = [
  { value: 'email',            label: 'Email',            iconKey: 'email',       iconColor: 'var(--primary)' },
  { value: 'linkedin_connect', label: 'LinkedIn Connect', icon: linkedinFaviconSmall },
  { value: 'voicemail',        label: 'Voicemail',        iconKey: 'voicemail',   iconColor: '#8b5cf6' },
  { value: 'wait',             label: 'Wait',             iconKey: 'wait' },
]

const props = defineProps({
  initialName: { type: String, default: '' },
  initialDescription: { type: String, default: '' },
  initialSteps: { type: Array, default: () => [] },
  locked: { type: Boolean, default: false },
  saveError: { type: String, default: '' },
  // Limits the step types offered; null offers them all.
  stepTypes: { type: Array, default: null },
})

const stepTypeOptions = props.stepTypes
  ? allStepTypeOptions.filter((o) => props.stepTypes.includes(o.value))
  : allStepTypeOptions

const emit = defineEmits(['update:form'])

let nextKey = 0
function makeKey() { return ++nextKey }

const form = reactive({
  name: props.initialName,
  description: props.initialDescription,
  steps: (props.initialSteps || []).map((s) => ({
    _key: makeKey(),
    step_index: s.step_index ?? 0,
    step_type: s.step_type || 'email',
    brief: s.brief || '',
    wait_days: s.wait_days || null,
    wait_unit: s.wait_unit || 'days',
  })),
})

/** Expose the form so the parent can read it on save. */
defineExpose({ form })

// ── Drag state ──────────────────────────────────────────────────
const dragState = reactive({ draggingIdx: null, overSlot: null })
const justDroppedIdx = ref(null)
const justCancelledIdx = ref(null)
let justDroppedTimer = null
let justCancelledTimer = null
const preventDocDrag = (e) => e.preventDefault()
let dragCommitted = false

function isNoOpSlot(slotIdx) {
  const d = dragState.draggingIdx
  if (d === null) return false
  return slotIdx === d || slotIdx === d + 1
}

function commitDrop(fromIdx, slotIdx) {
  dragCommitted = true
  const insertAt = fromIdx < slotIdx ? slotIdx - 1 : slotIdx
  const [moved] = form.steps.splice(fromIdx, 1)
  form.steps.splice(insertAt, 0, moved)
  renumber()
  clearTimeout(justDroppedTimer)
  justDroppedIdx.value = insertAt
  justDroppedTimer = setTimeout(() => { justDroppedIdx.value = null }, 600)
}

function onDragStart(idx, e) {
  e.dataTransfer.effectAllowed = 'move'
  e.dataTransfer.setData('text/plain', String(idx))
  dragCommitted = false
  document.addEventListener('dragover', preventDocDrag)
  setTimeout(() => { dragState.draggingIdx = idx }, 0)
}

function onDragEnd() {
  document.removeEventListener('dragover', preventDocDrag)
  const fromIdx = dragState.draggingIdx
  const slotIdx = dragState.overSlot
  const noOp = isNoOpSlot(slotIdx)
  dragState.draggingIdx = null
  dragState.overSlot = null
  if (dragCommitted) return
  if (slotIdx !== null && fromIdx !== null && !noOp) {
    commitDrop(fromIdx, slotIdx)
  } else if (fromIdx !== null) {
    clearTimeout(justCancelledTimer)
    justCancelledIdx.value = fromIdx
    justCancelledTimer = setTimeout(() => { justCancelledIdx.value = null }, 350)
  }
}

function onSlotDrop(slotIdx) {
  const fromIdx = dragState.draggingIdx
  if (fromIdx === null) return
  commitDrop(fromIdx, slotIdx)
  dragState.draggingIdx = null
  dragState.overSlot = null
}

function insertStep(atIdx) {
  form.steps.splice(atIdx, 0, {
    _key: makeKey(),
    step_index: atIdx,
    step_type: 'email',
    brief: '',
    wait_days: null,
    wait_unit: 'days',
  })
  renumber()
}

function removeStep(idx) {
  form.steps.splice(idx, 1)
  renumber()
}

function renumber() {
  form.steps.forEach((s, i) => { s.step_index = i })
}

function onTypeSelectChange(step, newType) {
  step.step_type = newType
  if (newType === 'wait') {
    step.brief = ''
    if (!step.wait_days) step.wait_days = 3
    if (!step.wait_unit) step.wait_unit = 'days'
  } else {
    step.wait_days = null
    step.wait_unit = 'days'
  }
}

function briefPlaceholder(type) {
  if (type === 'email') return 'Brief for the agent: what to focus on, tone, key points…'
  if (type === 'linkedin_connect') return 'What message should accompany the connection request?'
  if (type === 'voicemail') return 'What should the voicemail say?'
  return ''
}
</script>

<style scoped>
.seqEditor {
  --border: var(--color-border-subtle);
  --border2: var(--color-border-subtle);
  --surface: var(--color-bg-surface);
  --bg: var(--color-bg-app);
  --text: var(--color-text-primary);
  --muted: var(--color-text-tertiary);
  --primary: var(--color-accent);
  --success: var(--color-success);
  --danger: var(--color-danger);
  --warning: var(--color-warning, #f59e0b);
  --shadow: rgba(0,0,0,0.08);
}

.muted { color: var(--muted); }

.errorBanner {
  padding: 10px 14px;
  margin-bottom: 16px;
  background: color-mix(in srgb, var(--danger) 10%, var(--surface));
  border: 1px solid color-mix(in srgb, var(--danger) 30%, var(--border));
  border-radius: 8px;
  font-size: 13px;
  color: var(--danger);
}

.lockedBanner {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 14px;
  margin-bottom: 20px;
  background: color-mix(in srgb, var(--warning) 10%, var(--surface));
  border: 1px solid color-mix(in srgb, var(--warning) 30%, var(--border));
  border-radius: 8px;
  font-size: 13px;
  color: var(--text);
  line-height: 1.45;
}

.stepsSection { max-width: 480px; }
.stepsSectionHeader { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.stepsSectionTitle { font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: var(--muted); }
.stepsCanvas { display: flex; flex-direction: column; }
.stepsEmpty { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 40px 24px; border: 1px dashed var(--border); border-radius: 10px; margin: 4px 0; }
.stepsEmptyText { font-size: 14px; color: var(--muted); }

.stepCard {
  display: flex; align-items: flex-start; gap: 12px; padding: 12px 14px;
  border: 1.5px solid var(--border); border-radius: 10px; background: var(--surface);
  position: relative; transition: box-shadow 0.15s;
}
.stepCard:not(.locked):hover { box-shadow: 0 2px 8px var(--shadow); }
.stepCard.dragging { opacity: 0.4; }
@keyframes dropLand { 0% { box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 45%, transparent); } 100% { box-shadow: 0 0 0 0px color-mix(in srgb, var(--primary) 0%, transparent); } }
.stepCard.justDropped { animation: dropLand 550ms ease-out forwards; }
@keyframes dragCancel { 0% { opacity: 0.4; transform: scale(0.97); } 65% { opacity: 1; transform: scale(1.015); } 100% { opacity: 1; transform: scale(1); } }
.stepCard.justCancelled { animation: dragCancel 300ms ease-out forwards; }

.stepFields { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 10px; }
.stepTypeRow { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.stepTypeSelectWrap { width: 180px; flex-shrink: 0; }
.waitDaysField { display: flex; align-items: center; gap: 6px; }
.waitInput { width: 70px; }
.waitUnitSelect { width: 72px; }
.stepBriefTextarea {
  width: 100%; padding: 8px 10px; border: 1px solid var(--border); border-radius: 8px;
  background: var(--bg); color: var(--text); font-size: 13px; resize: vertical;
  font-family: inherit; line-height: 1.45;
}
.stepBriefTextarea:focus { outline: none; border-color: var(--primary); }

.removeBtn {
  position: absolute; top: 10px; right: 10px; background: none; border: 1px solid transparent;
  border-radius: 5px; padding: 4px; cursor: pointer; color: var(--muted); display: flex; align-items: center;
}
.removeBtn:hover { background: color-mix(in srgb, var(--danger) 10%, var(--surface)); border-color: var(--danger); color: var(--danger); }

.stepType-email { border-color: var(--primary); background: color-mix(in srgb, var(--primary) 4%, var(--surface)); }
.stepType-voicemail { border-color: #8b5cf6; background: color-mix(in srgb, #8b5cf6 4%, var(--surface)); }
.stepType-linkedin_connect { border-color: #0A66C2; background: color-mix(in srgb, #0A66C2 4%, var(--surface)); }
.stepType-wait { border-color: var(--border); background: var(--surface); }

.stepConnector { display: flex; flex-direction: column; align-items: center; }
.slotHint { width: 100%; border-top: 1px dashed var(--border2); margin: 4px 0; opacity: 0.6; }
.dropGhost { width: 100%; min-height: 50px; margin: 10px 0; border: 2px dashed var(--border2); border-radius: 10px; background: transparent; transition: border-color 0.1s, background 0.1s; box-sizing: border-box; }
.stepConnector.activeSlot .dropGhost { border-color: var(--primary); background: color-mix(in srgb, var(--primary) 5%, transparent); }
.connLine { width: 2px; height: 14px; background: var(--border); border-radius: 1px; flex-shrink: 0; }
.connInsertBtn {
  width: 22px; height: 22px; border-radius: 50%; border: 1px solid var(--border);
  background: var(--surface); color: var(--muted); cursor: pointer; display: flex;
  align-items: center; justify-content: center; flex-shrink: 0;
  transition: background 0.1s, border-color 0.1s, color 0.1s; padding: 0;
}
.connInsertBtn:hover { background: var(--primary); border-color: var(--primary); color: #fff; }
</style>
